/*
 * Copyright (c) 2016 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.axis

import com.macrofocus.common.collection.TreeSet
import com.macrofocus.common.collection.UniversalComparator
import com.macrofocus.common.filter.MutableFilter
import com.macrofocus.common.selection.Selection
import com.macrofocus.common.selection.SingleSelection
import org.molap.dataframe.DataFrame
import org.molap.series.Series
import org.molap.subset.SubsetDataFrame
import kotlin.math.ceil
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.min

class GenericAxisModel<Row, C>(
    dataFrame: DataFrame<Row, C, *>,
    annotationDataFrame: DataFrame<*, C, *>?,
    subsetDataFrame: SubsetDataFrame<Row, C, *>?,
    column: C,
    probing: SingleSelection<Row>,
    selection: Selection<Row>,
    filter: MutableFilter<Row>
) : DefaultAxisModel<Row, C>(dataFrame, annotationDataFrame, subsetDataFrame, column, probing, selection, filter) {
    private var list: List<Any>? = null
    private var index: MutableMap<Any, Int>? = null
    override val isNumerical: Boolean
        get() = false

    override fun getValue(row: Row): Number? {
        return getValue(com.macrofocus.high_d.axis.AxisModel.Companion.DATA, row)
    }

    override fun getValue(layer: Int, row: Row): Number? {
        val index = getIndex()
        val v: Any? = series.get(layer)!!.get(row)
        return index!![v]
    }

    override var minimum: Double
        get() = 0.0
        set(minimum) {
            super.minimum = minimum
        }
    override var maximum: Double
        get() = (getIndex()!!.size - 1).toDouble()
        set(maximum) {
            super.maximum = maximum
        }
    override val formattedMinimum: String
        get() {
            val l = getList()
            val value = if (l!!.size > 0) l[max(0, minimum.toInt())] else null
            return value.toString()
        }
    override val formattedMaximum: String
        get() {
            val l = getList()
            val value = if (l!!.size > 0) l[min(getIndex()!!.size - 1, maximum.toInt())] else null
            return value.toString()
        }

    override fun getFormattedValue(row: Row): String {
        return getFormattedValue(AxisModel.Companion.DATA, row)!!
    }

    override fun getFormattedValue(layer: Int, row: Row): String {
        val value: Any? = series.get(layer)!!.get(row)
        return if (value != null) value.toString() else ""
    }

    override fun getFormattedValue(value: Double): String {
        val max: Double = max(value, minimum)
        val min: Double = min(max, maximum)
        val v = getList()!![min.toInt()]
        return if (v != null) v.toString() else ""
    }

    override fun getOriginalFloorValue(value: Double): Any? {
        val list = getList()
        return if (!list!!.isEmpty()) {
//        value = (getList().size() - 1) - value;
            val max: Double = max(value, minimum)
            val min: Double = min(max, maximum)
            list[floor(min).toInt()]
        } else {
            null
        }
    }

    override fun getOriginalCeilValue(value: Double): Any? {
        val list = getList()
        return if (!list!!.isEmpty()) {
//        value = (getList().size() - 1) - value;
            val max: Double = max(value, minimum)
            val min: Double = min(max, maximum)
            list[ceil(min).toInt()]
        } else {
            null
        }
    }

    private fun getList(): List<Any>? {
        if (index == null) {
            createIndex()
        }
        return list
    }

    private fun getIndex(): Map<Any, Int>? {
        if (index == null) {
            createIndex()
        }
        return index
    }

    private fun createIndex() {
        val set: MutableSet<Any> = TreeSet<Any>(UniversalComparator().reversed() as Comparator<Any>)
        val series: Series<Row,out Any?>? = this.series.get(AxisModel.Companion.DATA)
        if (series != null) {
            for (r in dataFrame.rows()) {
                val value: Any? = series.get(r)
                if (value != null && value is Comparable<*>) {
                    set.add(value)
                }
            }
        }
        list = ArrayList<Any>(set)
        index = HashMap<Any, Int>()
        var i = 0
        for (o in list!!) {
            index!![o] = i
            i++
        }
    }
}